import type { TFunction } from 'i18next';
import type { AnalyzeRequest, DRBFMInputData } from '../types';
import { apiErrorDetail } from '../utils/apiErrorMessages';

const failureKeys: Readonly<Record<string, string>> = Object.assign(Object.create(null), {
  analysis_in_progress: 'analysisInProgress',
  rerun_would_discard_reviewed_rows: 'rerunBlocked',
  source_changed_since_analysis: 'sourceChanged',
  source_version_changed: 'sourceVersionChanged',
  source_not_found: 'sourceMissing',
  source_forbidden: 'sourceForbidden',
  source_extraction_pending: 'sourcePending',
  source_extraction_failed: 'sourceFailed',
  source_format_unsupported: 'sourceUnsupported',
  source_text_empty: 'sourceEmpty',
  source_too_large: 'sourceTooLarge',
  analysis_failed: 'analysisFailed',
  supplement_copied_into_rows: 'supplementCopied',
});

export const analysisFailureMessage = (t: TFunction, detail: unknown, fallback = 'analysisFailed'): string => {
  const code = typeof detail === 'string' ? detail
    : (detail as { code?: unknown } | null)?.code;
  const key = typeof code === 'string' ? failureKeys[code] : undefined;
  return t(`drbfm.firstRun.${key ?? fallback}`);
};

const failureDetail = (error: unknown): unknown => apiErrorDetail(error)
    ?? (error as { details?: { detail?: unknown } })?.details?.detail
    ?? (error as { message?: unknown })?.message
    ?? error;

export const analysisCommandFailure = (t: TFunction, error: unknown): string =>
  analysisFailureMessage(t, failureDetail(error), 'initializationFailed');

export const isAnalysisSourceFailure = (error: unknown): boolean => {
  const detail = failureDetail(error);
  const code = typeof detail === 'string' ? detail : (detail as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code.startsWith('source_') && !!failureKeys[code];
};

export const analyzeRequestBody = (request: AnalyzeRequest): Record<string, unknown> => {
  const labels = request.predefinedPartLabels ?? [];
  if (request.sources) {
    if (request.oldSpecification !== undefined || request.newSpecification !== undefined) {
      throw new Error('invalid_analysis_input');
    }
    const source = request.sources;
    const versions = source.expectedVersions ? { expected_versions: source.expectedVersions } : {};
    const supplement = source.supplement?.trim() ?? '';
    if (Array.from(supplement).length > 8000 || supplement.includes('\0')) {
      throw new Error('invalid_analysis_input');
    }
    const textRoles: string[] =
      source.pattern === 'old_new_text'
        ? ['old', 'new']
        : source.pattern === 'change_report_text'
          ? ['report']
          : source.pattern === 'old_new_mixed'
            ? [source.oldDocumentId ? 'new' : 'old']
            : [];
    const origins: Record<string, { document_id: string; version_id: string }> = {};
    for (const [role, origin] of Object.entries(source.derivedFrom ?? {})) {
      if (
        !textRoles.includes(role) ||
        !origin?.documentId?.trim() ||
        !origin.versionId?.trim() ||
        origin.versionId.length > 200
      )
        throw new Error('invalid_analysis_input');
      origins[role] = {
        document_id: origin.documentId,
        version_id: origin.versionId,
      };
    }
    const derived = Object.keys(origins).length ? { derived_from: origins } : {};
    if (source.pattern === 'old_new_mixed') {
      const docRole = source.oldDocumentId !== undefined ? 'old' : 'new';
      const text = docRole === 'old' ? source.newText : source.oldText;
      const document = docRole === 'old' ? source.oldDocumentId : source.newDocumentId;
      if (
        !document?.trim() ||
        source.reportDocumentId !== undefined ||
        source.reportText !== undefined ||
        (docRole === 'old'
          ? source.newDocumentId !== undefined || source.oldText !== undefined
          : source.oldDocumentId !== undefined || source.newText !== undefined) ||
        !text?.trim() ||
        Array.from(text).length > 100000 ||
        text.includes('\0') ||
        Object.keys(source.expectedVersions ?? {}).length !== 1 ||
        !source.expectedVersions?.[docRole]?.trim()
      )
        throw new Error('invalid_analysis_input');
      return {
        sources: {
          pattern: source.pattern,
          supplement,
          ...versions,
          ...derived,
          ...(docRole === 'old'
            ? { old_document_id: document, new_text: text }
            : { old_text: text, new_document_id: document }),
        },
        predefined_part_labels: labels,
      };
    }
    if (source.pattern === 'old_new_text' || source.pattern === 'change_report_text') {
      const texts = source.pattern === 'old_new_text' ? [source.oldText, source.newText] : [source.reportText];
      if (source.expectedVersions || source.oldDocumentId || source.newDocumentId || source.reportDocumentId
        || texts.some(text => !text?.trim() || Array.from(text).length > 100000 || text.includes('\0'))) {
        throw new Error('invalid_analysis_input');
      }
      return {
        sources: {
          pattern: source.pattern,
          supplement,
          ...derived,
          ...(source.pattern === 'old_new_text'
            ? { old_text: source.oldText, new_text: source.newText }
            : { report_text: source.reportText }),
        },
        predefined_part_labels: labels,
      };
    }
    if (source.pattern === 'old_new_documents') {
      if (!source.oldDocumentId?.trim() || !source.newDocumentId?.trim() || source.reportDocumentId) {
        throw new Error('invalid_analysis_input');
      }
      return { sources: { pattern: source.pattern, old_document_id: source.oldDocumentId,
        new_document_id: source.newDocumentId, supplement, ...versions }, predefined_part_labels: labels };
    }
    if (source.pattern !== 'change_report' || !source.reportDocumentId?.trim()
      || source.oldDocumentId || source.newDocumentId) throw new Error('invalid_analysis_input');
    return { sources: { pattern: source.pattern, report_document_id: source.reportDocumentId,
      supplement, ...versions }, predefined_part_labels: labels };
  }
  if (!request.oldSpecification?.trim() || !request.newSpecification?.trim()) {
    throw new Error('invalid_analysis_input');
  }
  return { old_specification: request.oldSpecification, new_specification: request.newSpecification,
    predefined_part_labels: labels };
};

/** The explicit mode wins; legacy selected documents are never submitted as pasted originals. */
export const analysisRequestFromInput = (input: DRBFMInputData): AnalyzeRequest => {
  if (!input.itemName.trim()) throw new Error('invalid_analysis_input');
  const request: AnalyzeRequest = input.analysisRequest ?? (
    input.specOldDocumentId || input.specNewDocumentId
      ? { sources: { pattern: 'old_new_documents', oldDocumentId: input.specOldDocumentId ?? '',
        newDocumentId: input.specNewDocumentId ?? '' } }
      : { oldSpecification: input.oldSpecification, newSpecification: input.newSpecification }
  );
  analyzeRequestBody(request);
  return { ...request, cadModelId: input.cadModelId, predefinedPartLabels: input.predefinedPartLabels };
};
